// Attachment component for handling file uploads
import { AttachmentUpload } from '@/components_v2/inputs/AttachmentUpload';
import AttachmentPreview from '@/components_v2/WebForm/components/QuestionContent/Attachment/Attachment.Preview';
import { QuestionModel } from '@/modules/core/domain/interfaces/IFormulary';
import useField from '@/modules/WebForm/hooks/useField';
import { createAnswer } from '@/modules/WebForm/utils';
import { HStack, Wrap, WrapItem } from '@chakra-ui/react';
import React from 'react';

import FormControlComponent from '../FormControlComponent';

const AttachmentContent: React.FC<QuestionModel> = () => {
  const { error, onChange, fieldState, clear } = useField();
  const [urls, setUrls] = React.useState<string[]>([]);
  const [fileNames, setFileNames] = React.useState<Record<string, string>>({});

  const value = React.useMemo(() => {
    if (fieldState) {
      if (Array.isArray(fieldState)) {
        setUrls(fieldState.map((v) => v.content));
        return fieldState;
      }
      setUrls([fieldState].map((v) => v.content));
      return [fieldState];
    }
    setUrls([]);
    return [];
  }, [fieldState]);

  const handleChange = React.useCallback(
    (newValue: string[], newFileNames?: Record<string, string>) => {
      if (newValue.length === 0) {
        clear();
      } else {
        const diff = newValue.filter(
          (n) => !value.find((v) => v.content === n),
        );
        const remains = value.filter((v) => {
          return newValue.includes(v.content);
        });

        // Update fileNames state with new file names
        if (newFileNames) {
          setFileNames((prev) => ({ ...prev, ...newFileNames }));
        }

        // Create answers with fileName for new attachments
        const newAnswers = diff.map((v) => {
          const fileName = newFileNames?.[v] || '';
          return createAnswer(v, undefined, fileName);
        });

        onChange([...remains, ...newAnswers]);
      }
    },
    [onChange, clear, value],
  );

  const onRemove = React.useCallback(
    (index: number) => {
      setUrls((prev) => {
        const urlToRemove = prev[index];
        const newUrls = prev
          .slice(0, index)
          .concat(prev.slice(index + 1, prev.length));

        // Remove filename from the map
        const updatedFileNames = { ...fileNames };
        delete updatedFileNames[urlToRemove];
        setFileNames(updatedFileNames);

        // Pass the updated fileNames to the parent component
        handleChange(newUrls, updatedFileNames);
        return newUrls;
      });
    },
    [handleChange, fileNames],
  );

  const onDownload = React.useCallback((url: string) => {
    window.open(url, '_blank');
  }, []);

  return (
    <FormControlComponent isInvalid={error}>
      <HStack>
        <Wrap>
          <WrapItem alignItems="center">
            <AttachmentUpload
              urls={urls}
              setUrls={setUrls}
              onChange={handleChange}
              maxFiles={20}
            />
          </WrapItem>
          {value.map((v, index) => (
            <WrapItem key={v.id}>
              <AttachmentPreview
                url={v.content}
                fileName={fileNames[v.content]}
                onRemove={() => onRemove(index)}
                onDownload={() => onDownload(v.content)}
              />
            </WrapItem>
          ))}
        </Wrap>
      </HStack>
    </FormControlComponent>
  );
};

export default AttachmentContent;
